import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, QrCode, Copy, Check, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useCurrency } from "@/hooks/useUserCurrency";

interface PixCheckoutDialogProps {
  amount: number;
  description: string;
  trigger: React.ReactNode;
}

export const PixCheckoutDialog = ({ amount, description, trigger }: PixCheckoutDialogProps) => {
  const [open, setOpen] = useState(false);
  const { format } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [form, setForm] = useState({
    name: "",
    cellphone: "",
    email: "",
    taxId: "",
  });
  const [qrData, setQrData] = useState<{
    brCodeBase64: string;
    brCode: string;
    id: string;
  } | null>(null);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const formatCPF = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handleCreateQRCode = async () => {
    if (!form.name || !form.email || !form.cellphone || !form.taxId) {
      toast.error("Preencha todos os campos");
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke("create-pix-qrcode", {
        body: {
          amount,
          description,
          customer: {
            name: form.name,
            cellphone: form.cellphone,
            email: form.email,
            taxId: form.taxId,
          },
        },
      });

      if (error) throw new Error(error.message);

      if (data?.data) {
        setQrData({
          brCodeBase64: data.data.brCodeBase64,
          brCode: data.data.brCode,
          id: data.data.id,
        });
        toast.success("QR Code gerado com sucesso!");
      } else {
        throw new Error("Resposta inválida da API");
      }
    } catch (err) {
      console.error("PIX QR Code error:", err);
      toast.error("Erro ao gerar QR Code. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    if (qrData?.brCode) {
      await navigator.clipboard.writeText(qrData.brCode);
      setCopied(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCheckPayment = async () => {
    if (!qrData?.id) return;

    try {
      setCheckingPayment(true);
      const { data, error } = await supabase.functions.invoke("check-pix-status", {
        body: { id: qrData.id, email: form.email },
      });

      if (error) throw new Error(error.message);

      if (data?.isPaid) {
        setPaymentConfirmed(true);
        toast.success("Obrigado seu Plano Pro esta Ativo!");
      } else {
        toast.info("Pagamento ainda não identificado. Tente novamente em alguns instantes.");
      }
    } catch (err) {
      console.error("Check payment error:", err);
      toast.error("Erro ao verificar pagamento. Tente novamente.");
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setQrData(null);
    setForm({ name: "", cellphone: "", email: "", taxId: "" });
    setCopied(false);
    setPaymentConfirmed(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleClose())}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            Pagamento via PIX
          </DialogTitle>
        </DialogHeader>

        {!qrData ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Valor: <strong>{format(amount)}</strong>
            </p>

            <div className="space-y-3">
              <div>
                <Label htmlFor="pix-name">Nome completo</Label>
                <Input
                  id="pix-name"
                  placeholder="Seu nome completo"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  maxLength={100}
                />
              </div>
              <div>
                <Label htmlFor="pix-phone">Telefone</Label>
                <Input
                  id="pix-phone"
                  placeholder="(11) 99999-9999"
                  value={form.cellphone}
                  onChange={(e) => handleChange("cellphone", formatPhone(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="pix-email">E-mail</Label>
                <Input
                  id="pix-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  maxLength={255}
                />
              </div>
              <div>
                <Label htmlFor="pix-cpf">CPF</Label>
                <Input
                  id="pix-cpf"
                  placeholder="000.000.000-00"
                  value={form.taxId}
                  onChange={(e) => handleChange("taxId", formatCPF(e.target.value))}
                />
              </div>
            </div>

            <Button
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
              onClick={handleCreateQRCode}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <QrCode className="h-4 w-4 mr-2" />
              )}
              Criar QR Code
            </Button>
          </div>
        ) : paymentConfirmed ? (
          <div className="space-y-6 text-center py-4">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <div className="space-y-2">
              <h3 className="text-xl font-bold">Obrigado seu Plano Pro esta Ativo!</h3>
              <p className="text-muted-foreground">Pagamento confirmado. Acesso PRO ativado por 30 dias.</p>
            </div>
            <Button className="w-full" onClick={handleClose}>
              Fechar
            </Button>
          </div>
        ) : (
          <div className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              Escaneie o QR Code abaixo para pagar via PIX
            </p>

            <div className="flex justify-center">
              <img
                src={qrData.brCodeBase64}
                alt="QR Code PIX"
                className="w-64 h-64 rounded-lg border"
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Ou copie o código PIX:</p>
              <div className="flex gap-2">
                <Input
                  value={qrData.brCode}
                  readOnly
                  className="text-xs"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyCode}
                  title="Copiar código PIX"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <Button
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
              onClick={handleCheckPayment}
              disabled={checkingPayment}
            >
              {checkingPayment ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Já efetuei o pagamento
            </Button>

            <Button variant="outline" className="w-full" onClick={handleClose}>
              Fechar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
